import type { CourseModule, Lesson, Quiz } from "../types/course";
import { CONTENT_VERSION } from "./version";

/** Lightweight navigation/search metadata only: never import lesson bodies here. */
export type LessonMeta = Pick<
  Lesson,
  | "id"
  | "moduleId"
  | "order"
  | "title"
  | "objective"
  | "estimatedMinutes"
  | "kind"
  | "keyPoints"
>;
export type ModuleMeta = Pick<
  CourseModule,
  "id" | "order" | "title" | "summary"
> & {
  lessons: LessonMeta[];
  quiz: Omit<Quiz, "questions"> & { questionIds: string[] };
};
export interface CourseCatalog {
  id: string;
  schemaVersion: 1;
  contentVersion: string;
  modules: ModuleMeta[];
}

// Kept explicitly independent of the lazy modules. The content build gate
// compares every projected metadata field with all six complete modules.
export const catalog: CourseCatalog = {
  id: "frontend-learn-studio",
  schemaVersion: 1,
  contentVersion: CONTENT_VERSION,
  modules: [
    {
      id: "html-basics",
      order: 1,
      title: "HTML 与语义化",
      summary: "从文档结构开始，让内容表达得清楚。",
      lessons: [
        {
          id: "html-document",
          moduleId: "html-basics",
          order: 1,
          title: "第一个网页与文档结构",
          objective: "用有语义的标题、段落和列表制作学习卡片。",
          estimatedMinutes: 12,
          kind: "html",
          keyPoints: [
            "HTML 描述结构与含义，CSS 描述外观。",
            "main 包含主要内容，h1 表示主标题。",
            "列表项 li 应放在 ul 或 ol 中。",
          ],
        },
        {
          id: "html-semantics",
          moduleId: "html-basics",
          order: 2,
          title: "常用标签与语义化",
          objective: "根据内容职责组织笔记，并建立有效的页内导航。",
          estimatedMinutes: 14,
          kind: "html",
          keyPoints: [
            "语义化表达内容职责，不替代 CSS。",
            "链接用于跳转，按钮用于执行动作。",
            "标题层级依据内容的从属关系组织。",
          ],
        },
        {
          id: "html-forms",
          moduleId: "html-basics",
          order: 3,
          title: "表单与可访问性",
          objective: "为邮箱输入建立标签关联，并设置必要的输入约束。",
          estimatedMinutes: 15,
          kind: "html",
          keyPoints: [
            "for 与 id 让标签和字段建立关联。",
            "placeholder 不能替代标签。",
            "客户端约束不等于服务器安全校验。",
          ],
        },
      ],
      quiz: {
        id: "html-quiz",
        moduleId: "html-basics",
        title: "HTML 与语义化 · 模块测评",
        passScorePercent: 70,
        questionIds: [
          "html-main",
          "html-heading",
          "html-card-code",
          "html-semantics-multi",
          "html-label-fill",
          "html-button-type",
          "html-required",
          "html-link-target",
        ],
      },
    },
    {
      id: "css-layout",
      order: 2,
      title: "CSS 布局与响应式",
      summary: "从盒模型到弹性与网格布局，让页面在不同空间里都可阅读。",
      lessons: [
        {
          id: "css-box-model",
          moduleId: "css-layout",
          order: 1,
          title: "盒模型与选择器",
          objective: "分清卡片内外空间，并用准确的类选择器控制边框外缘宽度。",
          estimatedMinutes: 15,
          kind: "css",
          keyPoints: [
            "content-box 的宽度不包含内边距与边框。",
            "border-box 不包含外边距。",
            "类选择器应限定到需要修改的组件。",
          ],
        },
        {
          id: "css-flexbox",
          moduleId: "css-layout",
          order: 2,
          title: "Flexbox 实战",
          objective: "用嵌套弹性容器排列工具栏和操作按钮。",
          estimatedMinutes: 18,
          kind: "css",
          keyPoints: [
            "弹性容器管理直接子元素。",
            "justify-content 与 align-items 分别对应主轴和交叉轴。",
            "gap 表达间隔，不改变文档顺序。",
          ],
        },
        {
          id: "css-grid",
          moduleId: "css-layout",
          order: 3,
          title: "Grid 布局",
          objective: "把六张课程卡片排列成两行三等列。",
          estimatedMinutes: 18,
          kind: "css",
          keyPoints: [
            "Grid 可共同控制行与列。",
            "fr 按份额分配轨道可用空间。",
            "gap 是轨道间隔，不是外圈留白。",
          ],
        },
        {
          id: "css-responsive",
          moduleId: "css-layout",
          order: 4,
          title: "响应式与媒体查询",
          objective: "让学习计划在 375px 显示一列、768px 显示两列。",
          estimatedMinutes: 18,
          kind: "css",
          keyPoints: [
            "从小屏基础布局开始，再逐步增强。",
            "媒体查询观察预览 iframe 自己的视口。",
            "断点由内容需要决定，不绑定设备品牌。",
          ],
        },
      ],
      quiz: {
        id: "css-layout-quiz",
        moduleId: "css-layout",
        title: "CSS 布局与响应式 · 模块测评",
        passScorePercent: 70,
        questionIds: [
          "css-box-width",
          "css-selectors",
          "css-flex-axis",
          "css-flex-align",
          "css-grid-tracks",
          "css-media-boundary",
          "css-responsive-rules",
          "css-grid-code",
        ],
      },
    },
    {
      id: "js-core",
      order: 3,
      title: "JavaScript 核心",
      summary: "理解数据、函数与异步，为交互与 React 建立基础。",
      lessons: [
        {
          id: "js-types",
          moduleId: "js-core",
          order: 1,
          title: "变量、类型与转换",
          objective: "识别字符串和数字，并进行明确的分钟数计算。",
          estimatedMinutes: 16,
          kind: "js",
          keyPoints: [
            "const 限制重新赋值，不等于冻结对象。",
            "加号可能是数字加法，也可能是字符串拼接。",
            "明确转换不等于完整的业务输入校验。",
          ],
        },
        {
          id: "js-arrays",
          moduleId: "js-core",
          order: 2,
          title: "数组常用方法",
          objective: "用 filter、map 和 reduce 生成未完成任务计划。",
          estimatedMinutes: 20,
          kind: "js",
          keyPoints: [
            "filter 选择，map 转换，reduce 汇总。",
            "大括号箭头函数需要显式 return。",
            "为 reduce 提供初始值，明确空数组结果。",
          ],
        },
        {
          id: "js-closures",
          moduleId: "js-core",
          order: 3,
          title: "函数、作用域与闭包",
          objective: "创建独立、可持续递增并支持不同起点的计数器。",
          estimatedMinutes: 22,
          kind: "js",
          keyPoints: [
            "函数参数提供输入，返回值交付结果。",
            "闭包保留对创建时词法环境的访问。",
            "每次工厂调用应创建独立状态。",
          ],
        },
        {
          id: "js-objects",
          moduleId: "js-core",
          order: 4,
          title: "对象、解构与展开",
          objective: "更新嵌套主题设置，同时保留原档案与其他字段。",
          estimatedMinutes: 19,
          kind: "js",
          keyPoints: [
            "解构提取属性，不自动深复制。",
            "展开是浅复制，同名属性由后面的覆盖。",
            "沿修改路径复制，并检查原数据未改变。",
          ],
        },
        {
          id: "js-async",
          moduleId: "js-core",
          order: 5,
          title: "异步：Promise 与 async/await",
          objective: "正确呈现加载、成功和失败，并在失败后重新加载。",
          estimatedMinutes: 22,
          kind: "js",
          keyPoints: [
            "Promise 表示未来结果，不是最终数据本身。",
            "await 暂停当前异步流程，不阻塞整个页面。",
            "失败不能伪装成空数据成功。",
          ],
        },
      ],
      quiz: {
        id: "js-core-quiz",
        moduleId: "js-core",
        title: "JavaScript 核心 · 模块测评",
        passScorePercent: 70,
        questionIds: [
          "js-number-convert",
          "js-array-tools",
          "js-const-object",
          "js-reduce-initial",
          "js-closure-output",
          "js-spread-copy",
          "js-await-error",
          "js-counter-code",
        ],
      },
    },
    {
      id: "dom-events",
      order: 4,
      title: "DOM 与事件",
      summary: "把数据与页面连接起来，完成可操作的待办清单。",
      lessons: [
        {
          id: "dom-nodes",
          moduleId: "dom-events",
          order: 1,
          title: "选取与操作节点",
          objective: "更新指定标题并添加不重复的列表项。",
          estimatedMinutes: 16,
          kind: "js",
          keyPoints: [
            "querySelector 可能返回 null。",
            "createElement 创建后还需要插入。",
            "普通文字使用 textContent，不当作 HTML 解析。",
          ],
        },
        {
          id: "dom-delegation",
          moduleId: "dom-events",
          order: 2,
          title: "事件与冒泡委托",
          objective: "用父容器监听处理已有和动态按钮的点击。",
          estimatedMinutes: 20,
          kind: "js",
          keyPoints: [
            "target 与 currentTarget 职责不同。",
            "用 closest 找操作目标，再检查容器范围。",
            "阻止默认动作不等于停止传播。",
          ],
        },
        {
          id: "dom-todo",
          moduleId: "dom-events",
          order: 3,
          title: "综合练习：待办清单",
          objective: "实现安全输入、增删与完成切换的完整清单。",
          estimatedMinutes: 28,
          kind: "js",
          keyPoints: [
            "数组记录状态，render 统一生成界面。",
            "稳定 id 避免删除后操作错位。",
            "验证新增、完成、撤销、删除与空状态。",
          ],
        },
      ],
      quiz: {
        id: "dom-events-quiz",
        moduleId: "dom-events",
        title: "DOM 与事件 · 模块测评",
        passScorePercent: 70,
        questionIds: [
          "dom-select-none",
          "dom-text-safe",
          "dom-targets",
          "dom-delegate",
          "dom-default",
          "dom-create-output",
          "dom-stable-id",
          "dom-todo-code",
        ],
      },
    },
    {
      id: "react-basics",
      order: 5,
      title: "React 基础",
      summary: "让组件响应操作，用状态描述变化。",
      lessons: [
        {
          id: "react-jsx",
          moduleId: "react-basics",
          order: 1,
          title: "JSX 与组件：组装一张书籍卡片",
          objective: "使用 JSX 和跨文件组件组合出完整页面。",
          estimatedMinutes: 15,
          kind: "react",
          keyPoints: [
            "组件名称以大写字母开头。",
            "花括号用于读取 JavaScript 表达式。",
            "导出方式需要与导入方式匹配。",
          ],
        },
        {
          id: "react-state",
          moduleId: "react-basics",
          order: 2,
          title: "Props 与状态：一个会记忆的计数器",
          objective: "使用 useState 响应点击，理解状态更新与界面的关系。",
          estimatedMinutes: 15,
          kind: "react",
          keyPoints: [
            "状态更新函数通知 React 重新渲染。",
            "事件属性接收函数，不在渲染期间调用更新。",
            "依赖前次状态时使用函数式更新。",
          ],
        },
        {
          id: "react-lists",
          moduleId: "react-basics",
          order: 3,
          title: "列表渲染与条件渲染",
          objective: "用 filter 和 map 展示未完成计划，并维护稳定身份。",
          estimatedMinutes: 15,
          kind: "react",
          keyPoints: [
            "filter 选择要展示的数据，map 描述每一项界面。",
            "稳定的 key 表达数据身份。",
            "为空的列表需要明确反馈。",
          ],
        },
        {
          id: "react-effects",
          moduleId: "react-basics",
          order: 4,
          title: "useEffect 与副作用：可停止的计时器",
          objective: "建立和清理定时器，让外部任务与当前状态保持同步。",
          estimatedMinutes: 15,
          kind: "react",
          keyPoints: [
            "Effect 用于与组件之外的系统同步。",
            "依赖列表如实表达响应式输入。",
            "清理与建立要成对，避免旧任务残留。",
          ],
        },
        {
          id: "react-forms",
          moduleId: "react-basics",
          order: 5,
          title: "表单与受控组件",
          objective: "把输入、问候和清空操作连接到同一份状态。",
          estimatedMinutes: 15,
          kind: "react",
          keyPoints: [
            "受控输入框的 value 和 onChange 成对使用。",
            "能从已有状态计算的内容无需再存一份。",
            "label 为输入框提供明确可访问名称。",
          ],
        },
      ],
      quiz: {
        id: "react-quiz",
        moduleId: "react-basics",
        title: "React 基础 · 模块测评",
        passScorePercent: 70,
        timeLimitMinutes: 20,
        questionIds: [
          "react-update",
          "react-click",
          "react-counter-code",
          "react-component-rules",
          "react-state-name",
          "react-map-output",
          "react-list-key",
          "react-effect-cleanup",
          "react-controlled-input",
        ],
      },
    },
    {
      id: "engineering",
      order: 6,
      title: "前端工程化",
      summary: "理解多文件工程，用模拟天气卡片串起组件与异步状态。",
      lessons: [
        {
          id: "engineering-modules",
          moduleId: "engineering",
          order: 1,
          title: "模块化与构建工具认知",
          objective: "修好跨文件温度格式化，区分模块接口、转译和生产构建。",
          estimatedMinutes: 20,
          kind: "react",
          keyPoints: [
            "默认导出与具名导出应匹配导入。",
            "目录 index 可作为统一模块出口。",
            "预览转译不代表类型检查或真实构建终端。",
          ],
        },
        {
          id: "engineering-weather",
          moduleId: "engineering",
          order: 2,
          title: "综合练习：天气卡片组件",
          objective: "实现可恢复的异步天气卡片，并防止过期结果覆盖新城市。",
          estimatedMinutes: 35,
          kind: "react",
          keyPoints: [
            "教学模拟数据不访问真实天气服务。",
            "互斥状态区分等待、成功、空结果和失败。",
            "Effect 成功与失败回调都要忽略过期结果。",
          ],
        },
      ],
      quiz: {
        id: "engineering-quiz",
        moduleId: "engineering",
        title: "前端工程化 · 模块测评",
        passScorePercent: 70,
        questionIds: [
          "eng-build-role",
          "eng-module-exports",
          "eng-ts-check",
          "eng-default-word",
          "eng-zero-output",
          "eng-cleanup-scope",
          "eng-latest-result",
          "eng-weather-code",
        ],
      },
    },
  ],
};

const moduleById = new Map(
  catalog.modules.map((module) => [module.id, module]),
);
const lessonById = new Map(
  catalog.modules.flatMap((module) =>
    module.lessons.map((lesson) => [lesson.id, lesson] as const),
  ),
);
const moduleByQuizId = new Map(
  catalog.modules.map((module) => [module.quiz.id, module]),
);

export function getModuleMeta(id: string): ModuleMeta | undefined {
  return moduleById.get(id);
}
export function getLessonMeta(id: string): LessonMeta | undefined {
  return lessonById.get(id);
}
export function getModuleByQuizId(id: string): ModuleMeta | undefined {
  return moduleByQuizId.get(id);
}
